// The screenshot tool on /tools/screenshot.
//
// Two jobs that people do in one sitting: cover up what should not be shared,
// then put the result on a background worth posting. Splitting them across two
// pages would mean saving a half-finished picture and picking it up again, so
// they sit on one canvas.
//
// The picture is redrawn from tools/compose.js on every change. Nothing is
// stored as a layer that could get out of step with what is on screen: the boxes
// are a list of rectangles, the rest is a handful of numbers, and the drawing is
// derived from both every time.

import { compose, layout, BACKDROPS } from './tools/compose.js';
import { decode, sizeOf, encode, renameExtension, saveBlob, formatBytes } from './tools/images.js';
import { wirePicker, blockStrayDrops, initColourPicker } from './tools/picker.js';
import { wireCopy } from './tools/copy.js';
import { wireAccountSave } from './tools/account.js';

// What the preview is drawn at. Big enough to judge the result, small enough
// that a slider stays smooth over a 4K screenshot.
const PREVIEW_WIDTH = 1200;

// The export is capped here. Past it browsers start refusing to allocate the
// canvas, and they do it by handing back a blank one rather than by failing.
const MAX_EXPORT_WIDTH = 6000;

// The picture drawn is bigger than the screenshot, sometimes much bigger: a tall
// window forced into 16:9 gains background on both sides until it is wider than
// it is long. Capping the screenshot's own width is therefore not enough, and
// the two limits that actually bite are the longest edge and the total area.
const MAX_CANVAS_EDGE = 8192;
const MAX_CANVAS_AREA = 40e6;

// Below this a drag was a click at somebody's idea of a steady hand, not an
// attempt to draw a box.
const MIN_REGION = 0.004;

const FORMATS = {
    'image/png':  { label: 'PNG',  extension: 'png',  quality: undefined },
    'image/webp': { label: 'WebP', extension: 'webp', quality: 0.92 },
    'image/jpeg': { label: 'JPEG', extension: 'jpg',  quality: 0.92 },
};

const el = id => document.getElementById(id);

const dom = {
    zone: el('shotZone'),
    input: el('shotInput'),
    empty: el('shotEmpty'),
    workspace: el('shotWorkspace'),
    stage: el('shotStage'),
    canvas: el('shotCanvas'),
    overlay: el('shotOverlay'),
    redactMode: el('shotRedactMode'),
    redactColourRow: el('shotRedactColourRow'),
    redactColour: el('shotRedactColour'),
    redactWarning: el('shotRedactWarning'),
    backdrop: el('shotBackdrop'),
    backdropColourRow: el('shotBackdropColourRow'),
    backdropColour: el('shotBackdropColour'),
    padding: el('shotPadding'),
    paddingValue: el('shotPaddingValue'),
    radius: el('shotRadius'),
    radiusValue: el('shotRadiusValue'),
    shadow: el('shotShadow'),
    bar: el('shotBar'),
    ratio: el('shotRatio'),
    format: el('shotFormat'),
    boxCount: el('shotBoxCount'),
    undo: el('shotUndo'),
    clearBoxes: el('shotClearBoxes'),
    save: el('shotSave'),
    reset: el('shotReset'),
    saveAccount: el('toolSaveAccount'),
    savePanel: el('toolSavePanel'),
    saveList: el('toolSaveList'),
    saveError: el('toolSaveError'),
    meta: el('shotMeta'),
    error: el('shotError'),
    live: el('shotLive'),
};

const state = {
    image: null,
    file: null,
    regions: [],
    redactMode: 'fill',
    redactColour: '#0f172a',
    backdrop: 'off',
    backdropColour: '#e2e8f0',
    padding: 0.06,
    radius: 0.014,
    shadow: 'soft',
    frame: 'none',
    barColour: '#e2e8f0',
    ratio: 'auto',
};

let composed = null;      // The last result from compose, for the pointer maths.
let selected = -1;        // Index into state.regions, or -1.
let drawing = null;       // The rectangle currently being dragged out.
let frameRequested = false;

/* ---------------------------------------------------------------- rendering */

function render() {
    if (!state.image) return;

    const source = sizeOf(state.image);
    composed = compose(state, Math.min(PREVIEW_WIDTH, source.width));

    dom.canvas.width = composed.canvas.width;
    dom.canvas.height = composed.canvas.height;
    dom.canvas.getContext('2d').drawImage(composed.canvas, 0, 0);

    dom.overlay.width = composed.canvas.width;
    dom.overlay.height = composed.canvas.height;
    drawOverlay();

    // The squares behind the preview only mean something while the picture
    // actually has nothing behind it.
    dom.stage.classList.toggle('is-transparent', state.backdrop === 'none' || state.backdrop === 'off');

    const out = exportSize();
    const capped = exportWidth() < Math.min(source.width, MAX_EXPORT_WIDTH);
    dom.meta.textContent = `${source.width} x ${source.height} source, saves at ${out.width} x ${out.height}`
        + (capped ? ' (limited to what a canvas can hold)' : '');
}

function schedule() {
    if (frameRequested) return;
    frameRequested = true;
    requestAnimationFrame(() => {
        frameRequested = false;
        render();
    });
}

/** The boxes, drawn over the picture so they can be picked up again. */
function drawOverlay() {
    if (!composed) return;

    const ctx = dom.overlay.getContext('2d');
    ctx.clearRect(0, 0, dom.overlay.width, dom.overlay.height);

    const line = Math.max(2, Math.round(composed.canvas.width / 600));
    const boxes = drawing ? [...state.regions, drawing] : state.regions;

    boxes.forEach((region, index) => {
        const box = toCanvas(region);
        const active = index === selected || (drawing && index === boxes.length - 1);

        // Two strokes, a dark one under a light one, so the outline is visible
        // whatever it happens to be sitting on.
        ctx.lineWidth = line + 2;
        ctx.strokeStyle = 'rgba(2, 6, 23, .55)';
        ctx.setLineDash([]);
        ctx.strokeRect(box.x, box.y, box.width, box.height);

        ctx.lineWidth = line;
        ctx.strokeStyle = active ? '#38bdf8' : 'rgba(255, 255, 255, .95)';
        ctx.setLineDash(active ? [] : [line * 3, line * 2]);
        ctx.strokeRect(box.x, box.y, box.width, box.height);
    });

    ctx.setLineDash([]);
}

/* -------------------------------------------------------- coordinate maths */

/** A stored region, in canvas pixels. */
function toCanvas(region) {
    const rect = composed.imageRect;
    return {
        x: rect.x + region.x * rect.width,
        y: rect.y + region.y * rect.height,
        width: region.w * rect.width,
        height: region.h * rect.height,
    };
}

/** A pointer event, as a position on the screenshot from 0 to 1. */
function toImage(event) {
    const rect = dom.overlay.getBoundingClientRect();
    const x = (event.clientX - rect.left) * (dom.overlay.width / rect.width);
    const y = (event.clientY - rect.top) * (dom.overlay.height / rect.height);

    const image = composed.imageRect;
    return {
        x: Math.min(1, Math.max(0, (x - image.x) / image.width)),
        y: Math.min(1, Math.max(0, (y - image.y) / image.height)),
    };
}

/** The last box drawn over this point, which is the one on top. */
function regionAt(point) {
    for (let i = state.regions.length - 1; i >= 0; i -= 1) {
        const r = state.regions[i];
        if (point.x >= r.x && point.x <= r.x + r.w && point.y >= r.y && point.y <= r.y + r.h) {
            return i;
        }
    }
    return -1;
}

/* ------------------------------------------------------------- interaction */

function wireDrawing() {
    let origin = null;
    let pointerId = null;

    dom.overlay.addEventListener('pointerdown', event => {
        if (!composed || (event.button !== 0 && event.pointerType === 'mouse')) return;
        pointerId = event.pointerId;
        dom.overlay.setPointerCapture(pointerId);
        origin = toImage(event);
        drawing = { x: origin.x, y: origin.y, w: 0, h: 0 };
    });

    dom.overlay.addEventListener('pointermove', event => {
        if (pointerId === null || event.pointerId !== pointerId) return;
        const point = toImage(event);
        drawing = {
            x: Math.min(origin.x, point.x),
            y: Math.min(origin.y, point.y),
            w: Math.abs(point.x - origin.x),
            h: Math.abs(point.y - origin.y),
        };
        drawOverlay();
    });

    const finish = event => {
        if (pointerId === null || (event && event.pointerId !== pointerId)) return;
        try {
            dom.overlay.releasePointerCapture(pointerId);
        } catch (err) {
            // The pointer is already gone, which is the state we wanted anyway.
        }
        pointerId = null;

        const box = drawing;
        drawing = null;

        if (box && box.w >= MIN_REGION && box.h >= MIN_REGION) {
            state.regions.push(box);
            selected = state.regions.length - 1;
            paintBoxCount();
            render();
            return;
        }

        // Too small to be a box, so it was a click: pick up whatever is there.
        selected = origin ? regionAt(origin) : -1;
        paintBoxCount();
        drawOverlay();
    };

    dom.overlay.addEventListener('pointerup', finish);
    dom.overlay.addEventListener('pointercancel', finish);

    document.addEventListener('keydown', event => {
        if (event.key !== 'Delete' && event.key !== 'Backspace') return;
        // Not while someone is typing a colour or a number.
        if (event.target.matches('input, textarea, select')) return;
        if (selected < 0) return;
        event.preventDefault();
        removeRegion(selected);
        announceBox(`Removed. ${state.regions.length} ${state.regions.length === 1 ? 'box' : 'boxes'} left.`);
    });
}

/* ------------------------------------------------------------- keyboard --- */

// One press moves or sizes a box by this share of the picture; with Alt, by a
// fifth of it, for the last pixel of a token's edge.
const KEY_STEP = 0.01;
const FINE_STEP = 0.002;

/** Say where the selected box is, in the picture's own pixels. */
function announceBox(prefix) {
    if (!dom.live || !state.image) return;
    if (selected < 0) {
        dom.live.textContent = prefix || 'No box selected.';
        return;
    }
    const source = sizeOf(state.image);
    const r = state.regions[selected];
    const px = value => Math.round(value);
    const text = `Box ${selected + 1} of ${state.regions.length}: `
        + `${px(r.w * source.width)} by ${px(r.h * source.height)} pixels, `
        + `${px(r.x * source.width)} from the left, ${px(r.y * source.height)} from the top.`;
    dom.live.textContent = prefix ? `${prefix} ${text}` : text;
}

/**
 * Cover something up without a pointer.
 *
 * The picture takes focus like any control. N drops a box in the middle,
 * arrows move it, Shift and arrows size it, Enter steps to the next box,
 * Escape lets go and Delete (handled in wireDrawing) removes it. Every change
 * is read out with the box's size and place, which is how someone who cannot
 * see the box can still tell that it covers the token.
 */
function wireKeyboard() {
    dom.overlay.addEventListener('keydown', event => {
        if (!composed) return;
        const step = event.altKey ? FINE_STEP : KEY_STEP;
        const box = selected >= 0 ? state.regions[selected] : null;
        let changed = false;
        let prefix = '';

        switch (event.key) {
        case 'n':
        case 'N': {
            // Staggered, so a second box does not land exactly on the first.
            const offset = (state.regions.length % 5) * 0.03;
            state.regions.push({ x: 0.4 + offset, y: 0.4 + offset, w: 0.2, h: 0.1 });
            selected = state.regions.length - 1;
            changed = true;
            prefix = 'Added.';
            break;
        }
        case 'Enter':
            if (!state.regions.length) return;
            selected = (selected + 1) % state.regions.length;
            break;
        case 'Escape':
            if (selected < 0) return;
            selected = -1;
            break;
        case 'ArrowLeft':
        case 'ArrowRight':
        case 'ArrowUp':
        case 'ArrowDown': {
            if (!box) return;
            const dx = event.key === 'ArrowLeft' ? -step : event.key === 'ArrowRight' ? step : 0;
            const dy = event.key === 'ArrowUp' ? -step : event.key === 'ArrowDown' ? step : 0;
            if (event.shiftKey) {
                box.w = Math.min(1 - box.x, Math.max(MIN_REGION, box.w + dx));
                box.h = Math.min(1 - box.y, Math.max(MIN_REGION, box.h + dy));
            } else {
                box.x = Math.min(1 - box.w, Math.max(0, box.x + dx));
                box.y = Math.min(1 - box.h, Math.max(0, box.y + dy));
            }
            changed = true;
            break;
        }
        default:
            return;
        }

        event.preventDefault();
        paintBoxCount();
        if (changed) schedule();
        else drawOverlay();
        announceBox(prefix);
    });
}

function removeRegion(index) {
    state.regions.splice(index, 1);
    selected = -1;
    paintBoxCount();
    render();
}

function paintBoxCount() {
    const count = state.regions.length;
    dom.boxCount.textContent = count === 0
        ? 'Drag across the picture to cover something up.'
        : `${count} ${count === 1 ? 'box' : 'boxes'}${selected >= 0 ? ', one selected. Delete removes it.' : '.'}`;
    dom.undo.disabled = count === 0;
    dom.clearBoxes.disabled = count === 0;
}

/* ---------------------------------------------------------------- controls */

function syncControls() {
    dom.redactColourRow.hidden = state.redactMode !== 'fill';
    dom.backdropColourRow.hidden = state.backdrop !== 'solid';
    dom.paddingValue.textContent = `${Math.round(state.padding * 100)}%`;
    dom.radiusValue.textContent = `${Math.round(state.radius * 1000) / 10}%`;

    // Without a backdrop the rest of the card has nothing to act on; see
    // dressed() in tools/compose.js. Disabled rather than hidden, so it is
    // clear what choosing a backdrop would open up.
    const plain = state.backdrop === 'off';
    for (const control of [dom.padding, dom.radius, dom.shadow, dom.bar, dom.ratio]) {
        control.disabled = plain;
        control.parentElement.classList.toggle('opacity-50', plain);
    }

    // The one thing about this page that is a security claim rather than a
    // preference, so it is stated plainly and only where it applies.
    dom.redactWarning.hidden = state.redactMode === 'fill';
}

/**
 * The width to draw the screenshot at when saving.
 *
 * Every measurement in the layout is a multiple of this width, so a canvas that
 * came out twice as tall as allowed is fixed by halving it. Area grows with the
 * square, hence the root. One correction is exact rather than approximate,
 * because nothing in the layout is anything but linear.
 */
function exportWidth() {
    const source = sizeOf(state.image);
    const width = Math.min(source.width, MAX_EXPORT_WIDTH);
    const box = layout(state, width);

    const over = Math.max(
        Math.max(box.outerW, box.outerH) / MAX_CANVAS_EDGE,
        Math.sqrt((box.outerW * box.outerH) / MAX_CANVAS_AREA),
        1
    );

    return over > 1 ? Math.max(1, Math.floor((width / over) * 0.995)) : width;
}

function exportSize() {
    const box = layout(state, exportWidth());
    return { width: box.outerW, height: box.outerH };
}

/**
 * Draw and encode the picture at full size.
 *
 * Shared by the download button and the account button, so the file that gets
 * uploaded is byte for byte the one that would have been downloaded.
 */
async function renderOutput() {
    const format = FORMATS[dom.format.value] || FORMATS['image/png'];
    const full = compose(state, exportWidth());
    const blob = await encode(full.canvas, dom.format.value, format.quality);
    return {
        blob,
        name: renameExtension(state.file.name, format.extension),
        width: full.canvas.width,
        height: full.canvas.height,
    };
}

async function save() {
    if (!state.image) return;

    dom.save.disabled = true;
    setError('');
    try {
        const out = await renderOutput();
        saveBlob(out.blob, out.name);
        dom.meta.textContent = `Saved ${out.width} x ${out.height}, ${formatBytes(out.blob.size)}`;
    } catch (err) {
        setError(err.message || 'Could not render the picture at full size');
    } finally {
        dom.save.disabled = false;
    }
}

function setError(message) {
    dom.error.hidden = !message;
    dom.error.textContent = message || '';
}

function reset() {
    if (state.image?.close) state.image.close();
    state.image = null;
    state.file = null;
    state.regions = [];
    selected = -1;
    drawing = null;
    composed = null;

    dom.workspace.hidden = true;
    dom.empty.hidden = false;
    dom.meta.textContent = '';
    setError('');
    paintBoxCount();
}

async function open([file]) {
    if (!file) return;
    reset();

    state.file = file;
    try {
        state.image = await decode(file);
    } catch (err) {
        setError(err.message || 'Could not read this image');
        return;
    }

    dom.empty.hidden = true;
    dom.workspace.hidden = false;
    render();
}

function fillBackdrops() {
    dom.backdrop.innerHTML = '';
    for (const backdrop of BACKDROPS) {
        const option = document.createElement('option');
        option.value = backdrop.id;
        option.textContent = backdrop.label;
        dom.backdrop.appendChild(option);
    }
    dom.backdrop.value = state.backdrop;
}

function init() {
    if (!dom.zone) return;

    blockStrayDrops();
    initColourPicker();
    wirePicker({ zone: dom.zone, input: dom.input, multiple: false, onFiles: open });
    wireDrawing();
    wireKeyboard();

    fillBackdrops();
    syncControls();
    paintBoxCount();

    const bind = (element, key, read, immediate) => {
        element.addEventListener('input', () => {
            state[key] = read(element);
            syncControls();
            if (immediate) render(); else schedule();
        });
    };

    bind(dom.redactMode, 'redactMode', e => e.value, true);
    bind(dom.redactColour, 'redactColour', e => e.value, false);
    bind(dom.backdrop, 'backdrop', e => e.value, true);
    bind(dom.backdropColour, 'backdropColour', e => e.value, false);
    bind(dom.padding, 'padding', e => Number(e.value) / 100, false);
    bind(dom.radius, 'radius', e => Number(e.value) / 1000, false);
    bind(dom.shadow, 'shadow', e => e.value, true);
    bind(dom.ratio, 'ratio', e => e.value, true);

    // One control for two values: whether there is a bar, and what shade it is.
    dom.bar.addEventListener('input', () => {
        state.frame = dom.bar.value === 'none' ? 'none' : 'bar';
        state.barColour = dom.bar.value === 'dark' ? '#1e293b' : '#e2e8f0';
        render();
    });

    dom.undo.addEventListener('click', () => {
        if (state.regions.length) removeRegion(state.regions.length - 1);
    });

    dom.clearBoxes.addEventListener('click', () => {
        state.regions = [];
        selected = -1;
        paintBoxCount();
        render();
    });

    dom.save.addEventListener('click', save);
    dom.reset.addEventListener('click', reset);
    wireCopy({ button: document.getElementById('shotCopy'), mirror: dom.save, collect: () => (state.image ? renderOutput() : null) });

    wireAccountSave({
        button: dom.saveAccount,
        panel: dom.savePanel,
        list: dom.saveList,
        error: dom.saveError,
        collect: async () => state.image ? [await renderOutput()] : [],
    });
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
} else {
    init();
}
